// Test ids (D-019, TEST_PLAN §1.4): `area.element[.part]`, lower-case, kebab-case inside a segment, dots
// between segments, flat (the full path in one string). They ship in demo and production builds — QA's
// Playwright specs select on them against dist/. Web renders `data-testid`; React Native renders `testID`,
// which react-native-web turns into the same attribute, so one selector set serves both apps.

// Live entity IDs are stable opaque keys issued by MetaEntityKey, not the demo's kebab-case slugs.
// Permit that exact key shape without accepting arbitrary underscores or relaxing the rest of the scheme.
const ENTITY_KEY = '(?:cmp|set|ad|crv)_[a-z2-7]{12}';
const SEGMENT = `(?:[a-z0-9]+(?:-[a-z0-9]+)*|${ENTITY_KEY})`;
const TEST_ID = new RegExp(`^${SEGMENT}(?:\\.${SEGMENT})+$`);

export function isValidTestId(id: string): boolean {
  return TEST_ID.test(id);
}

/**
 * Joins segments with dots and, in development and tests, rejects anything outside the scheme:
 * `testId('kpi', 'spent', 'value')` → `"kpi.spent.value"`; `testId('row.adset', slug, 'spent')` also works
 * because a segment may already carry dots. Entity ids are used as-is: demo slugs and opaque live keys.
 *
 * **The throw is the whole value of this function** (T-076 §4): a production build compiles the check out and
 * the helper degrades to a plain join, so an id built with a template literal forfeits the only protection the
 * scheme has. Every id in `apps/web` therefore comes through here — including the shared builders
 * (`lists/ListRow.tsx` `rowCellId`, `lists/DataTable.tsx`, `routes/campaigns/CampaignsPage.tsx`), which used
 * to interpolate — and `app/testIdScheme.test.tsx` walks the RENDERED document of the kitchen sink and of
 * every route's steady state so an id that never passes through a builder is caught as well (T-076-T2).
 */
export function testId(...segments: readonly (string | number)[]): string {
  const id = segments.map(String).join('.');
  if (import.meta.env.DEV && !isValidTestId(id)) {
    throw new Error(
      `Test id "${id}" breaks the D-019 scheme (area.element[.part]; lower-case; kebab-case inside a segment; dots between).`,
    );
  }
  return id;
}
